import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from './use-toast';
import { supabase } from '@/lib/supabase';

// Interfaccia flessibile per adattarsi a diverse strutture di tabelle
export interface Prenotazione {
  id: number;
  nome: string;
  email: string;
  telefono: string;
  data: string;
  ora: string;
  persone: number;
  note?: string;
  stato: 'in_attesa' | 'confermata' | 'rifiutata';
  creata_il: string;
}

export function usePrenotazioni() {
  const [prenotazioni, setPrenotazioni] = useState<Prenotazione[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const { toast } = useToast();
  const channelRef = useRef<any>(null);

  const fetchPrenotazioni = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    
    try {
      // Usa la tabella prenotazioni esistente
      const { data, error } = await supabase
        .from('prenotazioni')
        .select('*')
        .order('creata_il', { ascending: false });

      if (error) {
        throw error;
      }

      setPrenotazioni((data || []) as Prenotazione[]);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Errore nel caricamento delle prenotazioni:', error);
      }
      setErrore('Errore nel caricamento delle prenotazioni');
      toast({
        title: "Errore",
        description: "Impossibile caricare le prenotazioni",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Setup realtime subscription
  useEffect(() => {
    fetchPrenotazioni();

    // Cleanup previous channel if exists
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Subscribe to realtime changes
    const channel = supabase
      .channel('prenotazioni_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'prenotazioni'
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setPrenotazioni(prev => [payload.new as Prenotazione, ...prev]);
            toast({
              title: "Nuova prenotazione",
              description: `Nuova prenotazione da ${payload.new.nome}`,
            });
          } else if (payload.eventType === 'UPDATE') {
            setPrenotazioni(prev => 
              prev.map(p => p.id === payload.new.id ? payload.new as Prenotazione : p)
            );
            toast({
              title: "Prenotazione aggiornata",
              description: `Prenotazione di ${payload.new.nome} aggiornata`,
            });
          } else if (payload.eventType === 'DELETE') {
            setPrenotazioni(prev => prev.filter(p => p.id !== payload.old.id));
            toast({
              title: "Prenotazione eliminata",
              description: "Una prenotazione è stata eliminata",
            });
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [fetchPrenotazioni, toast]);

  const updatePrenotazione = async (id: number, updates: Partial<Prenotazione>, motivoRifiuto?: string) => {
    try {
      // If status is being updated to confermata or rifiutata, use edge function to send email
      if (updates.stato && (updates.stato === 'confermata' || updates.stato === 'rifiutata')) {
        const { error } = await supabase.functions.invoke('update-reservation-status', {
          body: {
            id,
            stato: updates.stato,
            motivo_rifiuto: motivoRifiuto || null
          }
        });

        if (error) {
          throw error;
        }

        toast({
          title: "Successo",
          description: `Prenotazione ${updates.stato === 'confermata' ? 'confermata' : 'rifiutata'} e email inviata`,
        });
      } else {
        // For other updates, use direct database update
        const { error } = await supabase
          .from('prenotazioni')
          .update(updates)
          .eq('id', id);

        if (error) {
          throw error;
        }

        toast({
          title: "Successo",
          description: "Prenotazione aggiornata con successo",
        });
      }
      
      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Errore nell\'aggiornamento:', error);
      }
      toast({
        title: "Errore",
        description: "Impossibile aggiornare la prenotazione",
        variant: "destructive",
      });
      return false;
    }
  };

  const deletePrenotazione = async (id: number) => {
    try {
      const { error } = await supabase
        .from('prenotazioni')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      toast({
        title: "Successo",
        description: "Prenotazione eliminata con successo",
      });
      
      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Errore nell\'eliminazione:', error);
      }
      toast({
        title: "Errore",
        description: "Impossibile eliminare la prenotazione",
        variant: "destructive",
      });
      return false;
    }
  };

  const addPrenotazione = async (prenotazione: Omit<Prenotazione, 'id' | 'creata_il'>) => {
    try {
      const { data, error } = await supabase
        .from('prenotazioni')
        .insert([prenotazione])
        .select()
        .single();

      if (error) {
        throw error;
      }

      toast({
        title: "Successo",
        description: "Prenotazione aggiunta con successo",
      });
      
      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Errore nell\'aggiunta:', error);
      }
      toast({
        title: "Errore",
        description: "Impossibile aggiungere la prenotazione",
        variant: "destructive",
      });
      return false;
    }
  };

  // Statistics
  const stats = {
    nuove: prenotazioni.filter(p => p.stato === 'in_attesa').length,
    confermate: prenotazioni.filter(p => p.stato === 'confermata').length,
    rifiutate: prenotazioni.filter(p => p.stato === 'rifiutata').length,
    oggi: prenotazioni.filter(p => p.data === new Date().toISOString().slice(0, 10)).length,
    totale: prenotazioni.length,
  };

  // Group by date
  const prenotazioniPerData = prenotazioni.reduce<Record<string, Prenotazione[]>>((acc, p) => {
    if (!acc[p.data]) {
      acc[p.data] = [];
    }
    acc[p.data].push(p);
    return acc;
  }, {});

  return {
    prenotazioni,
    prenotazioniPerData,
    loading,
    errore,
    stats,
    updatePrenotazione,
    deletePrenotazione,
    addPrenotazione,
    refetch: fetchPrenotazioni,
  };
} 